<template>
	<fragment v-if="hasInventoryItems">
		<div class="contractSection fontSmall marginTopSmall">
			<LoadingSpinner v-if="isSubmittingSignature || isResendingSignedContractEmail" />
			<div class="sectionTitle">Consigned Property List Signatures</div>
			<div class="contentColumn paddingTop paddingBottom contentColumnPaddingRight">
				Consignee:
				<br />
				Leland Little Auctions, Ltd.
				<br />
				620 Cornerstone Court
				<br />
				Hillsborough, NC 27278
				<br />
				(919) 644-1243
				<br />
				info@lelandlittle.com
				<br />
				LelandLittle.com
				<br />
				NCFL #7452; NCAL #6139
				<br />
				<br />
				Consignee Signature:
				<br />

				<fragment v-if="propertyListSignedByStaff">
					<span class="value contentBlock padding marginTopSmall">
						{{ propertyListStaffSignatureData.signed_by_name }}
					</span>
					<span
						v-if="propertyListStaffSignatureData.signed_date"
						class="contentBlock marginTopSmall fontSmall"
					>
						Signed on {{ formatDate(propertyListStaffSignatureData.signed_date) }} at
						{{ formatTime(propertyListStaffSignatureData.signed_date) }}
						<fragment v-if="isStaff">
							from a device with the IP address
							{{ propertyListStaffSignatureData.signed_remote_addr }}
							behind an HTTP proxy with the IP address
							{{ propertyListStaffSignatureData.signed_http_x_forwarded_for }}
						</fragment>
					</span>
				</fragment>
				<fragment v-else-if="isStaff">
					<template v-if="!isDesignatedPerson">
						<div class="signatureInputWrapper marginTopSmall">
              <input
								ref="consigneePropertyListSignatureInput"
								v-model="consigneeSignature"
								maxlength="250"
								name="consignee_signature"
								autocomplete="false"
								type="text"
								class="fixedWidthFont padding backgroundRed colorBlack signatureInput"
								placeholder="Only Designated Persons can Sign"
								disabled
							/>
            </div>
					</template>
					<template v-else>
						<div class="signatureInputWrapper marginTopSmall">
							<input
								ref="consigneePropertyListSignatureInput"
								v-model="consigneeSignature"
								maxlength="250"
								name="consignee_signature"
								autocomplete="false"
								type="text"
								class="fixedWidthFont padding backgroundRed colorBlack signatureInput"
								placeholder="Type your signature"
							/>
							<button
								class="signButton"
								:disabled="consigneeSignature.trim().length === 0"
								@click="handleArrowClick()"
							>
								Sign
							</button>
						</div>
					</template>
				</fragment>
			</div>
			<div class="contentColumn paddingTop paddingBottom contentColumnPaddingLeft">
				Consignor:
				<br />
				{{ contract.owner_name }}
				<br />
				{{ contract.owner_email }}
				<br /><br /><br /><br /><br /><br /><br />
				<div class="">Consignor Signature:</div>

				<fragment v-if="propertyListSignedByCustomer">
					<span class="value contentBlock padding marginTopSmall">
						{{ propertyListConsignorSignatureData.customer_signature }}
					</span>
					<span
						v-if="propertyListConsignorSignatureData.customer_signature_date"
						class="contentBlock marginTopSmall fontSmall"
					>
						Signed on
						{{ formatDate(propertyListConsignorSignatureData.customer_signature_date) }} at
						{{ formatTime(propertyListConsignorSignatureData.customer_signature_date) }}
						<fragment v-if="isStaff">
							from a device with the IP address
							{{ propertyListConsignorSignatureData.customer_signature_remote_addr }}
							behind an HTTP proxy with the IP address
							{{ propertyListConsignorSignatureData.customer_signature_http_x_forwarded_for }}
						</fragment>
					</span>
				</fragment>
				<fragment v-else>
					<div class="signatureInputWrapper marginTopSmall">
						<input
							id="consignor_signature"
							ref="consignorPropertyListSignatureInput"
							v-model="consignorSignature"
							maxlength="250"
							autocomplete="false"
							name="consignor_signature"
							class="fixedWidthFont padding backgroundRed colorBlack signatureInput"
							type="text"
							placeholder="Type your signature"
							:disabled="isStaff && propertyListSignedByStaff === false"
						/>
						<button
							class="signButton"
							:disabled="
								(isStaff && propertyListSignedByStaff === false) ||
								consignorSignature.trim().length === 0
							"
							@click="handleArrowClick()"
						>
							Sign
						</button>
					</div>
					<span class="contentBlock paddingTop fontSmall">
						I acknowledge that typing my name above has the same legal effect as signing my name.
					</span>
				</fragment>
			</div>
		</div>
	</fragment>
</template>

<script>
	import { Fragment } from 'vue-frag'
	import { mapMutations, mapActions } from 'vuex'
	import contractState from '@/mixins/contractState'
	import LoadingSpinner from '../components/LoadingSpinner.vue'

	export default {
		name: 'InventoryListSignatures',
		components: {
			Fragment,
			LoadingSpinner
		},
		mixins: [contractState],
		props: {
			contract: {
				type: Object,
				required: true
			},
			focusNextUnsignedInput: {
				type: Function,
				required: true
			},
			saveContract: {
				type: Function,
				required: true
			}
		},
		data() {
			return {
				consigneeSignature: '',
				consignorSignature: '',
				isSubmittingSignature: false,
				isResendingSignedContractEmail: false
			}
		},
		computed: {
			/**
			 * Whether any inventory lists are signed by staff
			 * @returns {boolean} True if any list is signed by staff, false otherwise
			 */
			propertyListSignedByStaff() {
				return this.contract.inventory_lists.some((list) => list.signed_by_name && list.signed_date)
			},

			/**
			 * Whether all inventory lists and contract are signed by customer
			 * @returns {boolean} True if everything is signed by customer
			 */
			propertyListSignedByCustomer() {
				return (
					this.propertyListConsignorSignatureData.customer_signature &&
					this.propertyListConsignorSignatureData.customer_signature_date
				)
			},

			/**
			 * Whether the contract has inventory items
			 * @returns {boolean} True if the contract has inventory items
			 */
			hasInventoryItems() {
				// True if any inventory_list has an id and items greater than 0.
				return this.contract.inventory_lists.some((list) => list.id && list.items?.length > 0)
			},

			/**
			 * Get signature data from the most recently signed inventory list by staff
			 * @returns {Object|null} Staff signature data or null if any list is unsigned
			 */
			propertyListStaffSignatureData() {
				// If no inventory lists or any list is unsigned by staff, return null
				if (!this.contract.inventory_lists?.length) {
					return {
						signed_by_name: null,
						signed_date: null,
						signed_remote_addr: null,
						signed_http_x_forwarded_for: null
					}
				}

				// Filter out empty lists and check if all remaining lists are signed
				const relevantLists = this.contract.inventory_lists.filter((list) => {
					// Include only lists with items or with an ID (saved lists)
					return list.id || (list.items?.length && list.items.some((item) => item.description))
				})

				// Check if any relevant list is unsigned
				const allSigned = relevantLists.every((list) => list.signed_by_name && list.signed_date)
				if (!allSigned) {
					return {
						signed_by_name: null,
						signed_date: null,
						signed_remote_addr: null,
						signed_http_x_forwarded_for: null
					}
				}

				// Find the list with the most recent signed_date
				const mostRecentList = relevantLists.reduce((latest, current) => {
					if (!latest || new Date(current.signed_date) > new Date(latest.signed_date)) {
						return current
					}
					return latest
				}, null)

				// Return data from the most recent list
				return {
					signed_by_name: mostRecentList?.signed_by_name || null,
					signed_date: mostRecentList?.signed_date || null,
					signed_remote_addr: mostRecentList?.signed_remote_addr || null,
					signed_http_x_forwarded_for: mostRecentList?.signed_http_x_forwarded_for || null
				}
			},

			/**
			 * Get signature data from the most recently signed inventory list by customer
			 * @returns {Object|null} Customer signature data or null if any list is unsigned
			 */
			propertyListConsignorSignatureData() {
				// If no inventory lists or any list is unsigned by customer, return null
				if (!this.contract.inventory_lists?.length) {
					return {
						customer_signature: null,
						customer_signature_date: null,
						customer_signature_remote_addr: null,
						customer_signature_http_x_forwarded_for: null
					}
				}

				// Filter out empty lists and check if all remaining lists are signed
				const relevantLists = this.contract.inventory_lists.filter((list) => {
					// Include only lists with items or with an ID (saved lists)
					return list.id || (list.items?.length && list.items.some((item) => item.description))
				})

				// Check if any relevant list is unsigned
				const allSigned = relevantLists.every(
					(list) => list.customer_signature && list.customer_signature_date
				)
				if (!allSigned) {
					return {
						customer_signature: null,
						customer_signature_date: null,
						customer_signature_remote_addr: null,
						customer_signature_http_x_forwarded_for: null
					}
				}

				// Find the list with the most recent customer_signature_date
				const mostRecentList = relevantLists.reduce((latest, current) => {
					if (
						!latest ||
						new Date(current.customer_signature_date) > new Date(latest.customer_signature_date)
					) {
						return current
					}
					return latest
				}, null)

				// Return data from the most recent list
				return {
					customer_signature: mostRecentList?.customer_signature || null,
					customer_signature_date: mostRecentList?.customer_signature_date || null,
					customer_signature_remote_addr: mostRecentList?.customer_signature_remote_addr || null,
					customer_signature_http_x_forwarded_for:
						mostRecentList?.customer_signature_http_x_forwarded_for || null
				}
			}
		},
		watch: {
			consigneeSignature(newSignature) {
				if (!this.isStaff) return
				if (!newSignature) return
				// Apply the signature to all unsigned inventory lists
				this.contract.inventory_lists.forEach((list) => {
					list.signed_by_name = newSignature
				})
				this.setContractData(this.contract)
			},
			consignorSignature(newSignature) {
				if (!this.isStaff) return
				if (!newSignature) return
				// Apply the signature to all unsigned inventory lists
				this.contract.inventory_lists.forEach((list) => {
					list.customer_signature = newSignature
				})
				this.setContractData(this.contract)
			}
		},
		methods: {
			...mapActions(['signContract']),
			...mapMutations({
				setContractData: 'SET_CONTRACT_DATA'
			}),
			/**
			 * Format the date
			 * @param {number} date - The date to format
			 * @returns {string} The formatted date
			 */
			formatDate(date) {
				let parsedDate = date
				if (typeof date === 'number') {
					// If timestamp is in milliseconds (13 digits), convert to seconds
					const timestamp = String(date).length === 13 ? Math.floor(date / 1000) : date
					parsedDate = new Date(timestamp * 1000)
				}
				return new Date(parsedDate).toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric'
				})
			},
			/**
			 * Format the time
			 * @param {number} date - The date to format
			 * @returns {string} The formatted time
			 */
			formatTime(date) {
				let parsedDate = date
				if (typeof date === 'number') {
					// If timestamp is in milliseconds (13 digits), convert to seconds
					const timestamp = String(date).length === 13 ? Math.floor(date / 1000) : date
					parsedDate = new Date(timestamp * 1000)
				}
				return new Date(parsedDate).toLocaleTimeString('en-US', {
					hour: 'numeric',
					minute: 'numeric',
					second: 'numeric',
					hour12: true
				})
			},
			/**
			 * Handles the click event on the next signature arrow.
			 * If staff, requests a save before attempting to focus next.
			 * If customer, directly attempts to focus next.
			 * @param {string} currentSignatureType - 'consignee' or 'consignor'
			 */
			async handleArrowClick() {
				if (this.isStaff) {
					// Request parent to save, passing a callback to focus next on success
					await this.saveContract()
					this.focusNextUnsignedInput()
				} else {
					// Sign the inventory list
					this.isSubmittingSignature = true
					await this.signContract({
						context: 'sign_inventory_list',
						token: this.contract.token,
						customerSignature: this.consignorSignature
					})
					this.focusNextUnsignedInput()
					this.isSubmittingSignature = false
				}
				this.consigneeSignature = ''
				this.consignorSignature = ''
			},
			/**
			 * Focus the next unsigned input
			 */
			focusNext() {
				const consigneePropertyListSignatureInput = this.$refs.consigneePropertyListSignatureInput
				const consignorPropertyListSignatureInput = this.$refs.consignorPropertyListSignatureInput

				if (consigneePropertyListSignatureInput) {
					consigneePropertyListSignatureInput.focus({ preventScroll: true })
					// Smooth scroll it into view
					consigneePropertyListSignatureInput.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					})
				} else if (consignorPropertyListSignatureInput) {
					consignorPropertyListSignatureInput.focus({ preventScroll: true })
					// Smooth scroll it into view
					consignorPropertyListSignatureInput.scrollIntoView({
						behavior: 'smooth',
						block: 'center'
					})
				}
			}
		}
	}
</script>

<style scoped></style>
